import { CarSpec } from 'noddi-types';
import { RegNumberSearchState, useAuthContext, useRegNumberSearch } from 'noddi-web';
import { useState } from 'react';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import { useWheelStorageOwnerGroup } from './useWheelStorageOwnerGroup';

type Options = {
  addCar: (car: CarSpec) => void;
  selectedCars: CarSpec[];
  userGroupId?: number;
};

export type BookingCarSearch = {
  plateSearch: RegNumberSearchState;
  /** Adds the car, or opens the wheel storage dialog instead; returns whether it was added. */
  selectCar: (car: CarSpec) => boolean;
  /** Looks up the typed plate and selects the car; resolves the car when it was added. */
  submitTypedPlate: () => Promise<CarSpec | undefined>;
  wheelStorageDialogCar: CarSpec | null;
  closeWheelStorageDialog: () => void;
  addCar: (car: CarSpec) => void;
};

/**
 * Car step search state, owned by the step page so its Next button can submit
 * a plate the customer typed but did not add.
 */
export const useBookingCarSearch = ({ addCar, selectedCars, userGroupId }: Options): BookingCarSearch => {
  const [wheelStorageDialogCar, setWheelStorageDialogCar] = useState<CarSpec | null>(null);
  const { currentBrand } = useCurrentBrand();
  const { currentUserGroupId: selectedUserGroupId } = useAuthContext();
  const currentUserGroupId = userGroupId ?? selectedUserGroupId;
  const getWheelStorageOwnerGroup = useWheelStorageOwnerGroup();

  const plateSearch = useRegNumberSearch({
    selectedLicensePlateNumbers: selectedCars.map((car) => car.licensePlate.number),
    brandDomain: currentBrand
  });

  const selectCar = (car: CarSpec) => {
    const wheelStorage = car.wheelStorage;

    // Add directly when there is no contract, or when it belongs to the user
    // group already selected. Otherwise the dialog either offers a user group
    // switch or explains that the car is out of reach.
    if (
      !wheelStorage?.length ||
      (getWheelStorageOwnerGroup(car) && wheelStorage.some((ws) => ws.userGroupId === currentUserGroupId))
    ) {
      addCar(car);
      return true;
    }

    setWheelStorageDialogCar(car);
    return false;
  };

  const submitTypedPlate = async () => {
    const car = await plateSearch.search();
    return car && selectCar(car) ? car : undefined;
  };

  return {
    plateSearch,
    selectCar,
    submitTypedPlate,
    wheelStorageDialogCar,
    closeWheelStorageDialog: () => setWheelStorageDialogCar(null),
    addCar
  };
};
